import { useQuery } from "@tanstack/react-query";
import {
  BookOpen,
  ChevronDown,
  CircleAlert,
  CircleUserRound,
  Clock3,
  CreditCard,
  FileText,
  LifeBuoy,
  LogOut,
  Menu,
  Server,
  Settings,
  WalletCards,
  X,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth, useCredentials } from "../../contexts/AuthContext";
import { useNotification } from "../../contexts/NotificationContext";
import { useViewSettings } from "../../contexts/SettingsContext";
import { hasBillingDetails, hasWaitingBillingDetails } from "../../domain/billing";
import { formatAutoLogoutRemaining, LAST_ACTIVITY_KEY } from "../../domain/session-timeout";
import { getBilling, getCredit } from "../../lib/api";
import { Brand } from "./Brand";
import { Button } from "../ui/Button";
import { Modal } from "../ui/Modal";

const navigation = [
  { to: "/app/servers", label: "Virtuální servery", icon: Server },
  { to: "/app/payments", label: "Platby a kredit", icon: WalletCards },
  { to: "/app/billing", label: "Fakturační údaje", icon: FileText },
  { to: "/app/account", label: "Nastavení účtu", icon: Settings },
];

const currentYear = new Date().getFullYear();

export function AppShell() {
  const credentials = useCredentials();
  const { logout } = useAuth();
  const { notify } = useNotification();
  const { autoLogoutMinutes } = useViewSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [logoutConfirmationOpen, setLogoutConfirmationOpen] = useState(false);
  const accountRef = useRef<HTMLDivElement>(null);
  const autoLogoutTriggered = useRef(false);
  const previousAutoLogoutMinutes = useRef(autoLogoutMinutes);
  const [logoutAt, setLogoutAt] = useState<number | null>(() => {
    if (autoLogoutMinutes === null) return null;
    const stored = Number(sessionStorage.getItem(LAST_ACTIVITY_KEY));
    const lastActivity = Number.isFinite(stored) && stored > 0 ? stored : Date.now();
    sessionStorage.setItem(LAST_ACTIVITY_KEY, String(lastActivity));
    return lastActivity + autoLogoutMinutes * 60_000;
  });
  const [clockNow, setClockNow] = useState(Date.now);
  const billing = useQuery({
    queryKey: ["billing", credentials.login],
    queryFn: () => getBilling(credentials),
    refetchInterval: 60_000,
  });
  const credit = useQuery({
    queryKey: ["credit", credentials.login],
    queryFn: () => getCredit(credentials),
    refetchInterval: 30_000,
    enabled: billing.data?.capabilities.frontend === true,
  });

  const resetInactivityCountdown = useCallback(() => {
    const now = Date.now();
    sessionStorage.setItem(LAST_ACTIVITY_KEY, String(now));
    autoLogoutTriggered.current = false;
    setClockNow(now);
    setLogoutAt(autoLogoutMinutes === null ? null : now + autoLogoutMinutes * 60_000);
  }, [autoLogoutMinutes]);

  useEffect(() => {
    if (previousAutoLogoutMinutes.current === autoLogoutMinutes) return;
    previousAutoLogoutMinutes.current = autoLogoutMinutes;
    resetInactivityCountdown();
  }, [autoLogoutMinutes, resetInactivityCountdown]);

  useEffect(() => {
    if (logoutAt === null) return;
    const updateCountdown = () => {
      const now = Date.now();
      setClockNow(now);
      if (now < logoutAt || autoLogoutTriggered.current) return;
      autoLogoutTriggered.current = true;
      notify("Byli jste automaticky odhlášeni z důvodu nečinnosti.", "info");
      logout();
      navigate("/auth/login", { replace: true });
    };
    updateCountdown();
    const interval = window.setInterval(updateCountdown, 1_000);
    return () => window.clearInterval(interval);
  }, [logoutAt, logout, navigate, notify]);

  useEffect(() => setMobileOpen(false), [location.pathname]);
  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (!accountRef.current?.contains(event.target as Node)) setAccountOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const billingMissing = billing.data !== undefined && !hasBillingDetails(billing.data);
  const billingWaiting = hasWaitingBillingDetails(billing.data);
  const remainingMs = logoutAt === null ? null : Math.max(0, logoutAt - clockNow);
  const remainingLabel = remainingMs === null ? null : formatAutoLogoutRemaining(remainingMs);

  const doLogout = () => {
    setLogoutConfirmationOpen(false);
    logout();
    navigate("/auth/login", { replace: true });
  };

  useEffect(() => {
    if (billingMissing && location.pathname !== "/app/billing") {
      navigate("/app/billing", { replace: true });
    }
  }, [billingMissing, location.pathname, navigate]);

  if (billing.data?.capabilities.frontend !== true) {
    const billingRoute = location.pathname === "/app/billing";
    return (
      <div className="billing-gate-shell" onClickCapture={resetInactivityCountdown}>
        <header className="billing-gate-shell__header">
          <Brand compact />
          <div><span>{credentials.login}</span><Button variant="ghost" size="sm" icon={<LogOut />} onClick={doLogout}>Odhlásit se</Button></div>
        </header>
        <main className="billing-gate-shell__main">
          {billingRoute ? <Outlet /> : billing.error ? (
            <div className="error-panel"><strong>Stav účtu se nepodařilo načíst</strong><p>Bez ověření stavu nelze správu cloudu zpřístupnit.</p><Button variant="primary" onClick={() => billing.refetch()}>Zkusit znovu</Button></div>
          ) : (
            <div className="loading-panel"><span className="spinner" /> Ověřuji stav fakturačních údajů…</div>
          )}
        </main>
      </div>
    );
  }

  return (
    <div className="app-shell" onClickCapture={resetInactivityCountdown}>
      <header className="topbar">
        <button className="icon-button topbar__menu" type="button" onClick={() => setMobileOpen(true)} aria-label="Otevřít menu">
          <Menu />
        </button>
        <Brand compact />
        <div className="topbar__right">
          <div className="credit-pill" aria-label="Aktuální kredit">
            <CreditCard />
            <span>
              <small>Kredit</small>
              <strong>{credit.data === undefined ? "—" : `${credit.data.toLocaleString("cs-CZ", { maximumFractionDigits: 2 })} Kč`}</strong>
            </span>
          </div>
          <Button variant="primary" size="sm" onClick={() => navigate("/app/payments")} icon={<WalletCards />}>
            Dobít kredit
          </Button>
          {remainingLabel !== null && (
            <button
              type="button"
              className={"session-countdown" + (remainingMs !== null && remainingMs <= 60_000 ? " session-countdown--warning" : "")}
              title="Zobrazit možnost odhlášení"
              aria-label={"Automatické odhlášení za " + remainingLabel + ". Odhlásit se"}
              aria-haspopup="dialog"
              onClick={() => setLogoutConfirmationOpen(true)}
            >
              <Clock3 aria-hidden="true" />
              <span><small>Odhlášení za</small><strong>{remainingLabel}</strong></span>
            </button>
          )}
          <div className="account-menu" ref={accountRef}>
            <button type="button" className="account-menu__trigger" onClick={() => setAccountOpen((value) => !value)} aria-expanded={accountOpen}>
              <CircleUserRound />
              <span>{credentials.login}</span>
              <ChevronDown />
            </button>
            {accountOpen && (
              <div className="account-menu__panel">
                <div className="account-menu__identity"><strong>{credentials.login}</strong><span>Zákaznický účet</span></div>
                <NavLink to="/app/account"><Settings /> Nastavení účtu</NavLink>
                <button type="button" onClick={doLogout}><LogOut /> Odhlásit se</button>
              </div>
            )}
          </div>
        </div>
      </header>

      <aside className={`sidebar ${mobileOpen ? "sidebar--open" : ""}`}>
        <div className="sidebar__mobile-head">
          <Brand compact />
          <button type="button" className="icon-button" onClick={() => setMobileOpen(false)} aria-label="Zavřít menu"><X /></button>
        </div>
        <nav aria-label="Hlavní navigace">
          <span className="nav-label">Správa cloudu</span>
          {navigation.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} className={({ isActive }) => (isActive ? "active" : "")}>
              <Icon /> <span>{label}</span>
              {to === "/app/billing" && billingMissing && <span className="nav-attention" role="img" aria-label="Fakturační údaje je nutné vyplnit" title="Fakturační údaje je nutné vyplnit"><CircleAlert /></span>}
            </NavLink>
          ))}
          <span className="nav-label nav-label--secondary">Nápověda</span>
          <NavLink to="/app/support" className={({ isActive }) => (isActive ? "active" : "")}><LifeBuoy /> <span>Podpora</span></NavLink>
          <a href="/specifikace_rest_api.pdf" target="_blank" rel="noreferrer"><BookOpen /> <span>REST API</span></a>
        </nav>
        <div className="sidebar__support">
          <LifeBuoy />
          <strong>Potřebujete poradit?</strong>
          <span>Jsme tu pro vás.</span>
          <a href="mailto:podpora@4smart.cz">Napsat podpoře</a>
        </div>
        <a className="sidebar__copyright" href="https://synesta.cz" target="_blank" rel="noreferrer">© {currentYear} synesta s.r.o.</a>
      </aside>
      {mobileOpen && <button className="sidebar-overlay" type="button" aria-label="Zavřít menu" onClick={() => setMobileOpen(false)} />}

      <main className="app-main">
        {(billingMissing || billingWaiting) && (
          <div className={`account-banner ${billingMissing ? "account-banner--warning" : "account-banner--info"}`}>
            <div>
              <FileText />
              <span>
                <strong>{billingMissing ? "Doplňte fakturační údaje" : "Fakturační údaje čekají na ověření"}</strong>
                <small>{billingMissing ? "Údaje potřebujeme pro zpřístupnění správy cloudu." : "Do ověření můžete vytvářet VPS pouze z instalačních ISO."}</small>
              </span>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate("/app/billing")}>
              {billingMissing ? "Doplnit údaje" : "Zobrazit stav"}
            </Button>
          </div>
        )}
        <Outlet />
      </main>

      <nav className="mobile-nav" aria-label="Mobilní navigace">
        {navigation.slice(0, 3).map(({ to, label, icon: Icon }) => (
          <NavLink key={to} to={to} className={({ isActive }) => (isActive ? "active" : "")}>
            <Icon /><span>{label === "Virtuální servery" ? "VPS" : label === "Platby a kredit" ? "Platby" : "Fakturace"}</span>
            {to === "/app/billing" && billingMissing && <span className="nav-attention" role="img" aria-label="Fakturační údaje je nutné vyplnit"><CircleAlert /></span>}
          </NavLink>
        ))}
        <button type="button" onClick={() => setMobileOpen(true)}><Menu /><span>Více</span></button>
      </nav>

      {logoutConfirmationOpen && (
        <Modal
          title="Odhlásit se z aplikace?"
          description="Opravdu chcete ukončit aktuální přihlášení?"
          danger
          onClose={() => setLogoutConfirmationOpen(false)}
          footer={<>
            <Button variant="ghost" onClick={() => setLogoutConfirmationOpen(false)}>Storno</Button>
            <Button variant="stop" icon={<LogOut />} onClick={doLogout}>Odhlásit se</Button>
          </>}
        />
      )}
    </div>
  );
}
